import { useQueries, useQuery } from "@tanstack/react-query";
import type { Capability, CapabilityRef } from "@agentsaas/protocol";
import { useServices } from "../app/services";

/** P7 能力包 / 密钥 / 外部 MCP / 媒体供应商的数据 */

export const capk = {
  all: ["cap"] as const,
  list: (tid: string, category: string, q: string) => ["cap", "list", tid, category, q] as const,
  one: (tid: string, capid: string, version: number | null) => ["cap", "one", tid, capid, version] as const,
  versions: (tid: string, capid: string) => ["cap", "versions", tid, capid] as const,
  agent: (aid: string) => ["cap", "agent", aid] as const,
  secrets: (tid: string) => ["secrets", tid] as const,
  servers: (tid: string) => ["mcp-servers", tid] as const,
  providers: ["media-providers"] as const,
  mediaKeys: (tid: string) => ["media-keys", tid] as const,
};

export function useCapabilities(tid: string, filter: { category?: string; q?: string } = {}) {
  const { api } = useServices();
  const category = filter.category ?? "";
  const q = filter.q ?? "";
  return useQuery({
    queryKey: capk.list(tid, category, q),
    enabled: !!tid,
    placeholderData: (p) => p,
    queryFn: async () => (await api.call("capabilityList", { query: { tenantId: tid, ...(category ? { category } : {}), ...(q ? { q } : {}) } })).items,
  });
}

export function useCapability(tid: string, capid: string | undefined, version: number | null = null) {
  const { api } = useServices();
  return useQuery({
    queryKey: capk.one(tid, capid ?? "", version),
    enabled: !!capid,
    retry: false,
    queryFn: () => api.call("capabilityGet", { params: { capid: capid! }, query: { tenantId: tid, ...(version ? { version } : {}) } }),
  });
}

export function useCapabilityVersions(tid: string, capid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: capk.versions(tid, capid ?? ""),
    enabled: !!capid,
    retry: false,
    queryFn: async () => (await api.call("capabilityVersions", { params: { capid: capid! }, query: { tenantId: tid } })).items,
  });
}

/** 一组引用各自解析到的能力包（模板编辑器的合并预览用）；key 为 `${id}@${version ?? "latest"}` */
export function useResolvedRefs(tid: string, refs: Pick<CapabilityRef, "id" | "version">[]) {
  const { api } = useServices();
  const uniq = [...new Map(refs.map((r) => [`${r.id}@${r.version ?? "latest"}`, r])).values()];
  const qs = useQueries({
    queries: uniq.map((r) => ({
      queryKey: capk.one(tid, r.id, r.version),
      retry: false,
      queryFn: () => api.call("capabilityGet", { params: { capid: r.id }, query: { tenantId: tid, ...(r.version ? { version: r.version } : {}) } }),
    })),
  });
  const map = new Map<string, Capability>();
  const missing = new Set<string>();
  uniq.forEach((r, i) => {
    const q = qs[i]!;
    const k = `${r.id}@${r.version ?? "latest"}`;
    if (q.data) map.set(k, q.data);
    else if (q.isError) missing.add(r.id);
  });
  return { map, missing, pending: qs.some((q) => q.isPending) };
}

export const refKey = (r: Pick<CapabilityRef, "id" | "version">) => `${r.id}@${r.version ?? "latest"}`;

export function useAgentCapabilities(aid: string) {
  const { api } = useServices();
  return useQuery({ queryKey: capk.agent(aid), enabled: !!aid, queryFn: () => api.call("agentCapabilitiesGet", { params: { aid } }) });
}

export function useSecrets(tid: string) {
  const { api } = useServices();
  return useQuery({ queryKey: capk.secrets(tid), enabled: !!tid, queryFn: async () => (await api.call("secretList", { params: { tid } })).items });
}

export function useMcpServers(tid: string) {
  const { api } = useServices();
  return useQuery({ queryKey: capk.servers(tid), enabled: !!tid, queryFn: async () => (await api.call("mcpServerList", { params: { tid } })).items });
}

export function useMediaProviders() {
  const { api } = useServices();
  return useQuery({ queryKey: capk.providers, staleTime: 5 * 60_000, queryFn: async () => (await api.call("mediaProviderList", {})).items });
}

export function useMediaKeys(tid: string) {
  const { api } = useServices();
  return useQuery({ queryKey: capk.mediaKeys(tid), enabled: !!tid, queryFn: async () => (await api.call("mediaKeyList", { params: { tid } })).items });
}
